import paper from '@turbowarp/paper';
import Modes from '../../lib/modes';

import { getRaster } from '../layer';
import { commitSelectionToBitmap } from '../bitmap';

import BoundingBoxTool from '../selection-tools/bounding-box-tool';
import NudgeTool from '../selection-tools/nudge-tool';
import SelectionBoxTool from '../selection-tools/selection-box-tool';

/**
 * paper.Tool that handles select mode in bitmap. This is made up of 2 subtools.
 * - The selection box tool is active when the user clicks an empty space and drags.
 *   It selects all items in the rectangle.
 * - The bounding box tool is active if the user clicks on a non-empty space. It handles
 *   reshaping the selection.
 */
class SelectTool extends paper.Tool {
  /** The distance within which mouse events count as a hit against an item */
  static get TOLERANCE() {
    return 2;
  }
  /**
   * @param {function} setSelectedItems Callback to set the set of selected items in the Redux state
   * @param {function} clearSelectedItems Callback to clear the set of selected items in the Redux state
   * @param {function} setCursor Callback to set the visible mouse cursor
   * @param {!function} onUpdateImage A callback to call when the image visibly changes
   */
  constructor(setSelectedItems, clearSelectedItems, setCursor, onUpdateImage) {
    super();
    this.onUpdateImage = onUpdateImage;
    this.boundingBoxTool = new BoundingBoxTool(
      Modes.BIT_SELECT,
      setSelectedItems,
      clearSelectedItems,
      setCursor,
      onUpdateImage
    );
    const nudgeTool = new NudgeTool(Modes.BIT_SELECT, this.boundingBoxTool, onUpdateImage);
    this.selectionBoxTool = new SelectionBoxTool(Modes.BIT_SELECT, setSelectedItems, clearSelectedItems);
    this.selectionBoxMode = false;
    this.selection = null;
    this.active = false;

    // We have to set these functions instead of just declaring them because
    // paper.js tools hook up the listeners in the setter functions.
    this.onMouseDown = this.handleMouseDown;
    this.onMouseDrag = this.handleMouseDrag;
    this.onMouseMove = this.handleMouseMove;
    this.onMouseUp = this.handleMouseUp;
    this.onKeyUp = nudgeTool.onKeyUp;
    this.onKeyDown = nudgeTool.onKeyDown;

    this.boundingBoxTool.setSelectionBounds();
  }
  /**
   * Should be called if the selection changes to update the bounds of the bounding box.
   * @param {Array<paper.Item>} selectedItems Array of selected items.
   */
  onSelectionChanged(selectedItems) {
    this.boundingBoxTool.onSelectionChanged(selectedItems);
    if (this.selection && this.selection.parent && !this.selection.selected) {
      // Selection got deselected
      this.commitSelection();
    }
    if (
      (!this.selection || !this.selection.parent) &&
      selectedItems &&
      selectedItems.length === 1 &&
      selectedItems[0] instanceof paper.Raster
    ) {
      // Track the new active selection. This may happen via undo, paste, or drag to select.
      this.selection = selectedItems[0];
    }
  }
  /**
   * Returns the hit options to use when conducting hit tests.
   * @return {object} See paper.Item.hitTest for definition of options
   */
  getHitOptions() {
    // Tolerance needs to be scaled when the view is zoomed in in order to represent the same
    // distance for the user to move the mouse.
    return {
      segments: true,
      stroke: true,
      curves: true,
      fill: true,
      guide: false,
      tolerance: SelectTool.TOLERANCE / paper.view.zoom,
      match: (hitResult) => {
        // Don't match helper items, unless they are handles.
        if (!hitResult.item.data || !hitResult.item.data.isHelperItem) return true;
        return hitResult.item.data.isScaleHandle || hitResult.item.data.isRotHandle;
      }
    };
  }
  handleMouseDown(event) {
    if (event.event.button > 0) return; // only first mouse button
    this.active = true;

    // If bounding box tool does not find an item that was hit, rasterize the old selection,
    // then use selection box tool.
    if (
      !this.boundingBoxTool.onMouseDown(
        event,
        event.modifiers.alt,
        event.modifiers.shift,
        false /* doubleClicked */,
        this.getHitOptions()
      )
    ) {
      this.commitSelection();
      this.selectionBoxMode = true;
      this.selectionBoxTool.onMouseDown(event.modifiers.shift);
    }
  }
  handleMouseDrag(event) {
    if (event.event.button > 0 || !this.active) return; // only first mouse button

    if (this.selectionBoxMode) {
      this.selectionBoxTool.onMouseDrag(event);
    } else {
      this.boundingBoxTool.onMouseDrag(event);
    }
  }
  handleMouseMove(event) {
    this.boundingBoxTool.onMouseMove(event, this.getHitOptions());
  }
  handleMouseUp(event) {
    if (event.event.button > 0 || !this.active) return; // only first mouse button

    if (this.selectionBoxMode) {
      this.selectionBoxTool.onMouseUpBitmap(event);
    } else {
      this.boundingBoxTool.onMouseUp(event);
    }
    this.selectionBoxMode = false;
    this.active = false;
  }
  commitSelection() {
    if (!this.selection || !this.selection.parent) return;

    commitSelectionToBitmap(this.selection, getRaster());
    this.selection.remove();
    this.selection = null;
    this.onUpdateImage();
  }
  deactivateTool() {
    this.commitSelection();
    this.boundingBoxTool.deactivateTool();
    this.boundingBoxTool = null;
    this.selectionBoxTool = null;
  }
}

export default SelectTool;
